<template>
  <div v-if="show" class="modal-wrapper">
    <div class="modal-backdrop" @click="closeModal"></div>
    <div class="modal-dialog modal-xl" role="dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ translate('interactions.customer_interaction_details', 'Customer Interaction Details') }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <!-- Positive Interaction Banner with Google Review -->
        <div v-if="interaction.rating >= 4 && !interaction.alertTriggered" class="alert alert-success m-0" role="alert">
          <i class="bi bi-star-fill me-2"></i>
          <strong>{{ translate('interactions.positive_feedback', 'Positive Feedback') }}</strong>
          <span class="ms-2">{{ translate('interactions.thank_you_message', 'Thank you for the positive feedback!') }}</span>
          <a
            v-if="reviewLink"
            :href="reviewLink"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-sm btn-light ms-3"
          >
            <i class="bi bi-star-fill me-1"></i>
            {{ translate('interactions.leave_review', 'Leave a review') }}
          </a>
        </div>
        <div v-if="interaction.alertTriggered" class="alert alert-danger m-0" role="alert">
          <strong>{{ translate('assistance.title', 'Customer Needs Assistance') }}</strong>
          <span class="ms-2">{{ translate('interactions.manager_takeover', 'The manager needs to take over this conversation.') }}</span>
          <button
            v-if="interaction.active === false"
            type="button"
            class="btn btn-sm btn-light ms-3"
            :disabled="reactivatingAgent"
            @click="reactivateAgent"
          >
            <span v-if="reactivatingAgent" class="spinner-border spinner-border-sm me-1"></span>
            {{ translate('interactions.reactivate_agent', 'Reactivate agent') }}
          </button>
        </div>
        <div v-if="reactivationError" class="alert alert-danger m-0">{{ reactivationError }}</div>
        <div class="modal-body p-0">
          <!-- Pet switcher: shown when customer has interactions for multiple pets -->
          <div v-if="siblingInteractions.length > 1" class="pet-switcher border-bottom px-3 pt-2 pb-1 bg-light">
            <ul class="nav nav-pills gap-1">
              <li v-for="s in siblingInteractions" :key="s.id" class="nav-item">
                <button
                  class="nav-link py-1 px-3"
                  :class="{ active: s.id === activePetId }"
                  @click="switchPet(s.id)"
                >
                  <i v-if="s.petName === 'ASSISTANCE'" class="bi bi-exclamation-triangle-fill text-warning me-1"></i>
                  <span v-else>🐾 </span>{{ s.petName || '?' }}
                </button>
              </li>
            </ul>
          </div>

          <!-- Mobile Tabs Header - only visible on mobile devices -->
          <div class="d-md-none border-bottom">
            <ul class="nav nav-tabs nav-fill">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'conversation' }" href="#" @click.prevent="activeTab = 'conversation'">
                  <i class="bi bi-chat-text me-1"></i> {{ translate('interactions.conversation', 'Conversation') }}
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'details' }" href="#" @click.prevent="activeTab = 'details'">
                  <i class="bi bi-info-circle me-1"></i> {{ translate('customers.details', 'Customer Details') }}
                </a>
              </li>
            </ul>
          </div>

          <div class="row g-0">
            <!-- Left Side: Conversation (Hidden on mobile if details tab is active) -->
            <div class="col-md-8 border-end" :class="{ 'd-none d-md-block': activeTab === 'details' }">
              <div class="chat-header border-bottom p-3">
                <div class="d-flex align-items-center">
                  <div class="avatar-circle bg-primary text-white me-3">
                    {{ customerDetails.name ? customerDetails.name.charAt(0).toUpperCase() : '?' }}
                  </div>
                  <div>
                    <h6 class="mb-0">{{ customerDetails.name || interaction.customerName || interaction.name || 'Unknown Customer' }}</h6>
                    <small class="text-muted">{{ formatDate(interaction.date) }}</small>
                  </div>
                </div>
              </div>
              
              <div class="conversation-container p-3">
                <div v-if="allMessages.length" class="messages">
                  <!-- Archived messages (oldConversations) -->
                  <template v-if="archivedMessages.length">
                    <div
                      v-for="(message, index) in archivedMessages"
                      :key="'archived-' + index"
                      :class="['message-wrapper', message.role === 'assistant' ? 'message-out' : 'message-in']"
                    >
                      <div class="message-bubble message-archived">
                        <div class="message-content">{{ message.message }}</div>
                      </div>
                    </div>
                    <!-- Session separator -->
                    <div class="session-separator">
                      <span class="session-separator-label">{{ translate('interactions.current_session', 'Current Session') }}</span>
                    </div>
                  </template>

                  <!-- Current session messages (conversation) -->
                  <div
                    v-for="(message, index) in currentMessages"
                    :key="'current-' + index"
                    :class="['message-wrapper', message.role === 'assistant' ? 'message-out' : 'message-in']"
                  >
                    <div class="message-bubble">
                      <div class="message-content">{{ message.message }}</div>
                      <div v-if="message.time" class="message-time">{{ formatTime(message.time) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-messages text-center text-muted py-5">
                  <i class="bi bi-chat-left-text fs-1 d-block mb-3"></i>
                  {{ translate('interactions.no_conversation_history', 'No conversation history available') }}
                </div>

                <!-- Message Input -->
                <div class="message-input-container border-top p-3">
                  <div v-if="sendError" class="alert alert-danger py-2 px-3 mb-2 small d-flex align-items-start gap-2">
                    <i class="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0"></i>
                    <span>{{ sendError }}</span>
                  </div>
                  <div v-if="sendSuccess" class="alert alert-success py-2 px-3 mb-2 small d-flex align-items-center gap-2">
                    <i class="bi bi-check-circle-fill flex-shrink-0"></i>
                    <span>{{ translate('interactions.message_sent', 'Message sent successfully') }}</span>
                  </div>
                  <div class="input-group">
                    <input
                      v-model="newMessage"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': sendError }"
                      :placeholder="translate('interactions.type_message', 'Type a message...')"
                      @keyup.enter="sendMessage"
                      :disabled="sendingMessage"
                    />
                    <button
                      class="btn btn-primary"
                      @click="sendMessage"
                      :disabled="!newMessage.trim() || sendingMessage"
                    >
                      <i v-if="sendingMessage" class="bi bi-arrow-repeat spin"></i>
                      <i v-else class="bi bi-send"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Right Side: Details (Hidden on mobile if conversation tab is active) -->
            <div class="col-md-4" :class="{ 'd-none d-md-block': activeTab === 'conversation' }">
              <div class="details-container p-3">
                <h6 class="section-title border-bottom pb-2 mb-3">{{ translate('customers.details', 'Customer Details') }}</h6>
                
                <div class="detail-item mb-3">
                  <div class="detail-label">{{ translate('interactions.customer', 'Customer') }}</div>
                  <div class="detail-value fw-bold">{{ customerDetails.name || interaction.customerName || interaction.name || translate('customers.unknown', 'N/A') }}</div>
                </div>
                
                <div class="detail-item mb-3">
                  <div class="detail-label">{{ translate('customers.phone', 'Phone') }}</div>
                  <div class="detail-value">{{ formatPhone(customerDetails.phone || interaction.phone) }}</div>
                </div>
                
                <div class="detail-item mb-3">
                  <div class="detail-label">{{ translate('customers.pets', 'Pets') }}</div>
                  <div class="detail-value">
                    <span v-if="customerDetails.pets && customerDetails.pets.length">
                      {{ customerDetails.pets.join(', ') }}
                    </span>
                    <span v-else-if="interaction.petName">{{ interaction.petName }}</span>
                    <span v-else>N/A</span>
                  </div>
                </div>
                
                <h6 class="section-title border-bottom pb-2 my-3">{{ translate('interactions.service_information', 'Service Information') }}</h6>
                
                <div class="detail-item mb-3">
                  <div class="detail-label">{{ translate('interactions.service_type', 'Service Type') }}</div>
                  <div class="detail-value">{{ interaction.service || 'N/A' }}</div>
                </div>
                
                <div class="detail-item mb-3">
                  <div class="detail-label">{{ translate('interactions.service_date', 'Service Date') }}</div>
                  <div class="detail-value">{{ formatDate(interaction.serviceDateTime) }}</div>
                </div>

                <div v-if="interaction.lastAgentMessageAt" class="detail-item mb-3">
                  <div class="detail-label">{{ translate('interactions.last_manager_message', 'Last manager message') }}</div>
                  <div class="detail-value">{{ formatDate(interaction.lastAgentMessageAt) }}</div>
                </div>

                <div v-if="interaction.reactivatedAt" class="detail-item mb-3">
                  <div class="detail-label">{{ translate('interactions.agent_reactivated_at', 'Agent reactivated at') }}</div>
                  <div class="detail-value">{{ formatDate(interaction.reactivatedAt) }}</div>
                </div>
                
                <div class="detail-item mb-3">
                  <div class="detail-label">{{ translate('interactions.customer_rating', 'Customer Rating') }}</div>
                  <div class="detail-value">
                    <div class="stars">
                      <i v-for="i in 5" :key="i" 
                         :class="['bi', i <= interaction.rating ? 'bi-star-fill' : 'bi-star']"
                         style="color: #F4A261;"
                      ></i>
                      <span class="ms-2 text-muted">({{ interaction.rating || 0 }}/5)</span>
                    </div>
                  </div>
                </div>
                
                <h6 class="section-title border-bottom pb-2 my-3">{{ translate('interactions.interaction_summary', 'Interaction Summary') }}</h6>
                <p class="mb-3">{{ interaction.summary || translate('interactions.no_summary_available', 'No summary available.') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Assistance Alert Modal -->
  <AssistanceAlertModal
    :show="showAssistanceModal"
    :interaction="interaction"
    @contact="handleAssistanceContact"
    @close="showAssistanceModal = false"
  />
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import AssistanceAlertModal from './AssistanceAlertModal.vue'

export default {
  name: 'InteractionDetailsModal',
  components: {
    AssistanceAlertModal
  },
  props: {
    interactionId: {
      type: String,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const store = useStore()
    
    // Use the translation getter from the Vuex store
    const translate = (key, fallback) => {
      return store.getters.translate(key, fallback)
    }
    
    // Fetch interaction data when interactionId changes
    const fetchInteractionData = async () => {
      if (props.interactionId) {
        await store.dispatch('fetchSingleInteraction', props.interactionId)
      }
    }
    
    // Watch for interactionId changes
    watch(() => props.interactionId, (newId) => {
      if (newId) {
        fetchInteractionData()
      } else {
        store.dispatch('clearActiveInteraction')
      }
    }, { immediate: true })
    
    // Watch for show changes to cleanup when modal is closed
    watch(() => props.show, (isShown) => {
      if (!isShown) {
        store.dispatch('clearActiveInteraction')
      } else if (props.interactionId) {
        fetchInteractionData()
      }
    })
    
    // Get interaction from store's activeInteraction
    const interaction = computed(() => {
      return store.state.activeInteraction || {}
    })
    
    const customerDetails = computed(() => {
      if (!interaction.value.customerId) return {}
      const customer = store.state.customers.find(
        c => c.id === interaction.value.customerId
      )
      return customer || {}
    })

    const reviewLink = computed(() => {
      const profile = store.state.users.find(profile => profile.userId === store.state.user?.uid)
      return profile?.reviewLink || ''
    })

    // Archived messages from oldConversations field (n8n structure)
    const archivedMessages = computed(() => {
      const old = interaction.value.oldConversations
      if (!Array.isArray(old)) return []
      return old.filter(m => m.role !== 'system')
    })

    // Current session messages from conversation field
    const currentMessages = computed(() => {
      const conv = interaction.value.conversation
      if (!Array.isArray(conv)) return []
      return conv.filter(m => m.role !== 'system')
    })

    // All messages combined for the empty-state check
    const allMessages = computed(() => [
      ...archivedMessages.value,
      ...currentMessages.value
    ])
    
    const activeTab = ref('conversation')
    const showAssistanceModal = ref(false)
    const hasShownAlert = ref(false)
    const reactivatingAgent = ref(false)
    const reactivationError = ref('')
    const newMessage = ref('')
    const sendingMessage = ref(false)
    const sendError = ref('')
    const sendSuccess = ref(false)

    // Watch for modal show to check for alerts (only show once per modal open)
    watch(() => props.show, (isShown) => {
      if (isShown && interaction.value.alertTriggered && !hasShownAlert.value) {
        showAssistanceModal.value = true
        hasShownAlert.value = true
      }
      // Reset the flag when modal closes
      if (!isShown) {
        hasShownAlert.value = false
      }
    })

    const handleAssistanceContact = () => {
      showAssistanceModal.value = false
    }

    const reactivateAgent = async () => {
      if (!interaction.value.id || reactivatingAgent.value) return
      reactivatingAgent.value = true
      reactivationError.value = ''
      try {
        const reactivatedAt = new Date().toISOString()
        const updated = await store.dispatch('updateInteraction', {
          interactionId: interaction.value.id,
          data: {
            active: true,
            alertTriggered: false,
            managerTakeover: false,
            reactivatedAt,
            lastMessageDate: Date.now()
          }
        })
        if (!updated) throw new Error('Could not reactivate the agent.')
      } catch (error) {
        reactivationError.value = error.message || 'Could not reactivate the agent.'
      } finally {
        reactivatingAgent.value = false
      }
    }

    const sendMessage = async () => {
      if (!newMessage.value.trim() || sendingMessage.value) return

      sendingMessage.value = true
      sendError.value = ''
      sendSuccess.value = false

      const message = newMessage.value.trim()
      const phone = interaction.value.phone || customerDetails.value.phone
      const interactionId = interaction.value.id
      const documentId = interaction.value.document_id

      console.log('[Chat] sendMessage called:', { phone, interactionId, documentId, message })

      if (!phone) {
        sendError.value = 'No phone number found for this interaction.'
        console.error('[Chat] No phone number available on interaction:', interaction.value)
        sendingMessage.value = false
        return
      }

      if (!interactionId) {
        sendError.value = 'No interaction ID found.'
        console.error('[Chat] No interactionId on interaction:', interaction.value)
        sendingMessage.value = false
        return
      }

      try {
        await store.dispatch('sendMessage', { phone, message, interactionId, documentId })
        newMessage.value = ''
        sendSuccess.value = true
        setTimeout(() => { sendSuccess.value = false }, 3000)
      } catch (error) {
        console.error('[Chat] Error sending message:', error)
        sendError.value = error.message || 'Failed to send message. Check console for details.'
      } finally {
        sendingMessage.value = false
      }
    }

    // Normalize any phone string to plain digits for comparison
    const toPlainPhone = (raw = '') =>
      (raw + '').replace('whatsapp:', '').replace('+', '').replace(/\D/g, '')

    // Extract phone digits from a doc ID formatted as {plainPhone}_{petSlug}
    const phoneFromId = (id = '') => {
      const underscore = (id + '').indexOf('_')
      return underscore > 0 ? (id + '').slice(0, underscore).replace(/\D/g, '') : ''
    }

    // ASSISTANCE interaction fetched directly from Firestore (n8n doesn't set userId so it's not in the store)
    const generalInteraction = ref(null)

    const fetchGeneralInteraction = async (phone) => {
      const plainPhone = toPlainPhone(phone)
      if (!plainPhone) return
      const assistanceId = `${plainPhone}_ASSISTANCE`
      try {
        const snap = await getDoc(doc(db, 'interactions', assistanceId))
        generalInteraction.value = snap.exists() ? { id: snap.id, ...snap.data() } : null
      } catch {
        generalInteraction.value = null
      }
    }

    // All interactions for the same phone (includes _ASSISTANCE docs not in the store)
    const siblingInteractions = computed(() => {
      const plainPhone =
        toPlainPhone(interaction.value.phone) ||
        toPlainPhone(customerDetails.value.phone) ||
        phoneFromId(interaction.value.id)
      if (!plainPhone) return []
      const fromStore = store.state.interactions
        .filter(i => {
          const iPhone = toPlainPhone(i.phone) || phoneFromId(i.id)
          return iPhone === plainPhone
        })
      if (generalInteraction.value && !fromStore.find(s => s.id === generalInteraction.value.id)) {
        return [...fromStore, generalInteraction.value]
          .sort((a, b) => (a.petName || '').localeCompare(b.petName || ''))
      }
      return fromStore
        .slice()
        .sort((a, b) => (a.petName || '').localeCompare(b.petName || ''))
    })

    const activePetId = ref(props.interactionId)

    watch(() => props.interactionId, (newId) => {
      if (newId) activePetId.value = newId
    })

    watch(() => interaction.value.phone, (phone) => {
      if (phone) fetchGeneralInteraction(phone)
    }, { immediate: true })

    watch(() => interaction.value.id, (id) => {
      if (id) fetchGeneralInteraction(phoneFromId(id))
    }, { immediate: true })

    const switchPet = async (id) => {
      activePetId.value = id
      await store.dispatch('fetchSingleInteraction', id)
    }
    
    return {
      interaction,
      customerDetails,
      reviewLink,
      activeTab,
      archivedMessages,
      currentMessages,
      allMessages,
      siblingInteractions,
      activePetId,
      switchPet,
      showAssistanceModal,
      handleAssistanceContact,
      reactivatingAgent,
      reactivationError,
      reactivateAgent,
      newMessage,
      sendingMessage,
      sendError,
      sendSuccess,
      sendMessage,
      closeModal: () => {
        store.dispatch('clearActiveInteraction')
        emit('update:show', false)
      },
      translate
    }
  },
  methods: {
    toJsDate(value) {
      if (!value) return null;
      if (value?.toDate) return value.toDate();       // Firestore Timestamp
      if (typeof value === 'number') return new Date(value); // unix ms
      return new Date(value);                          // ISO string
    },
    formatDate(date) {
      const d = this.toJsDate(date);
      if (!d || isNaN(d.getTime())) return 'N/A';
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatTime(date) {
      const d = this.toJsDate(date);
      if (!d || isNaN(d.getTime())) return '';
      return d.toLocaleString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatPhone(phone) {
      if (!phone) return 'N/A';
      let cleaned = String(phone).replace(/^whatsapp:/i, '');
      if (!cleaned.startsWith('+')) cleaned = '+' + cleaned;
      return cleaned;
    }
  }
}
</script>

<style scoped>
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal-dialog {
  position: relative;
  z-index: 1060;
  width: 100%;
  max-width: 1000px;
  margin: 1.75rem auto;
  pointer-events: none;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
  outline: 0;
  max-height: 90vh;
}

.modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: calc(0.3rem - 1px);
  border-top-right-radius: calc(0.3rem - 1px);
}

.modal-body {
  position: relative;
  flex: 1 1 auto;
}

.conversation-container {
  height: calc(70vh - 120px);
  overflow-y: auto;
  background-color: #f5f7fb;
}

.messages {
  display: flex;
  flex-direction: column;
}

.message-wrapper {
  display: flex;
  margin-bottom: 1rem;
  max-width: 85%;
}

.message-in {
  align-self: flex-start;
}

.message-out {
  align-self: flex-end;
}

.message-bubble {
  padding: 0.75rem;
  border-radius: 1rem;
  position: relative;
}

.message-in .message-bubble {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 0.25rem;
}

.message-out .message-bubble {
  background-color: #e9ecef;
  color: #212529;
  border-bottom-right-radius: 0.25rem;
}

.message-content {
  word-wrap: break-word;
}

.message-time {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.25rem;
  text-align: right;
}

.section-title {
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.detail-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.detail-value {
  font-size: 1rem;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
}

.chat-header {
  background-color: #ffffff;
}

.stars {
  display: flex;
  align-items: center;
}

.message-input-container {
  background-color: #f8f9fa;
}

.message-input-container .input-group {
  max-width: 100%;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.details-container {
  height: calc(70vh - 120px);
  overflow-y: auto;
  background-color: #f5f7fb;
}

p {
  font-size: 1rem; 
}

.session-separator {
  display: flex;
  align-items: center;
  margin: 1rem 0;
  gap: 0.75rem;
}

.session-separator::before,
.session-separator::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: #dee2e6;
}

.session-separator-label {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
  background-color: #f5f7fb;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  border: 1px solid #dee2e6;
}

.message-archived .message-content {
  opacity: 0.65;
}

.message-in .message-bubble.message-archived {
  background-color: #f0f0f0;
  border-color: #e0e0e0;
}

.message-out .message-bubble.message-archived {
  background-color: #dde1e7;
}
</style>
